import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { History } from "../../types";
import { Box, FlexColBox, Dot, Icon } from "../../common/components";
import { palette } from "../../styles/theme";
import { useMoment } from "../../common/hooks/useMoment";

interface Props {
  historyItem: History;
  isLast?: boolean;
}

const OrderHistory = ({ historyItem, isLast = false }: Props) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { formatFullDateWithTime } = useMoment();

  const user = historyItem?.User;
  const historyEvent = historyItem?.event;
  const eventName = historyEvent?.name;
  const historyEventData = historyItem?.event?.data;
  const oldCurrency = historyEventData?.old?.currency || "undefined";
  const newCurrency = historyEventData?.new?.currency || "undefined";

  const title =
    user?.firstName +
    " " +
    user?.lastName +
    " " +
    t(`order.history_actions.${eventName}`);
  const createdAt = formatFullDateWithTime(historyItem.createdAt);

  let content = "";

  // Depending on the event, format correctly
  switch (eventName) {
    case "currency_updated":
      content =
        "The currency was changed from " + oldCurrency + " to " + newCurrency; // TODO: translation
      break;
    default:
      content = JSON.stringify(historyItem?.event?.data);
  }

  const hasContent = content && content !== "{}";

  return (
    <Box display="flex" gap="12px" mb={isLast ? 0 : "32px"}>
      {/* Timeline line and dot */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        position="relative"
      >
        <Dot color={palette.grey.darkGrey} size={6} />
        {!isLast && (
          <Box
            sx={{
              position: "absolute",
              top: "6px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "1px",
              height: "calc(100% + 32px)",
              backgroundColor: palette.grey.darkGrey,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <FlexColBox gap="8px" flex={1} maxWidth="100%">
        <Box display="flex" alignItems="flex-start" gap="8px">
          {hasContent && (
            <Icon
              icon="keyboard_arrow_right"
              size={14}
              color={palette.grey.nearBlack}
              style={{
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                cursor: "pointer",
              }}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}

          <FlexColBox gap="4px" flex={1}>
            <Typography variant="textSmBold">{title}</Typography>
            <Typography variant="textXs" color={palette.grey.primary}>
              {createdAt}
            </Typography>
          </FlexColBox>
        </Box>

        {/* Details box */}
        {hasContent && isExpanded && (
          <Box
            sx={{
              ml: "18px",
              padding: "12px",
              backgroundColor: "white",
              border: `1px solid ${palette.grey.lightGrey}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="textSm"
              color={palette.grey.primary}
              sx={{
                wordBreak: "break-word",
              }}
            >
              {content}
            </Typography>
          </Box>
        )}
      </FlexColBox>
    </Box>
  );
};

export default OrderHistory;
